import type { ReactNode } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import type { FieldValues, Path } from 'react-hook-form';
import { fieldError, type FormFieldBinding } from './binding';
import { RecommendedFieldWarning } from './recommendedFieldWarning';

export interface TextFieldOptions {
    /**
     * An extra adornment of the field's own — a link out of the value, say.
     * Drawn after the unit and before the recommended-empty hint, which stays
     * outermost so it reads as being about the whole field.
     */
    endAdornment?: ReactNode;
    /** Values for a parameterized label ("Abstich zu Planum {number}"). */
    labelValues?: Record<string, string | number>;
    /**
     * Hold the label up although MUI thinks the field is empty. Needed by every
     * registered field a BUTTON can fill: `setValue` writes straight into the
     * input node, which fires no React change event, so the FormControl around
     * it never learns that it is filled and lays its label over the value.
     *
     * Pass whether the field currently HAS a value (the form has to watch it
     * anyway to render such a button); `false` leaves MUI's own behaviour
     * alone, so a field nobody fills from outside needs none of this.
     */
    shrinkLabel?: boolean;
    /** A note under the field. A validation error takes its place. */
    helperText?: ReactNode;
}

/**
 * A plain registered text field: label from the form's catalog, error and
 * message from its validation, and whatever the binding says the field states
 * about itself — its unit, and the amber hint while a recommended field is
 * empty.
 *
 * A form that declares neither `unitId` nor `recommended` gets exactly the bare
 * TextField the record dialogs each used to write out by hand.
 */
export function renderTextField<Values extends FieldValues>(
    binding: FormFieldBinding<Values>,
    name: Path<Values>,
    options: TextFieldOptions = {}
) {
    const error = fieldError(binding.errors, name);
    const unitId = binding.unitId?.(name);
    // A registered input is uncontrolled, so nothing here knows whether the
    // field is empty. Only the fields that CAN carry the hint get an element
    // that finds out for itself — and it re-renders alone, without the dialog
    // around it (see RecommendedFieldWarning).
    const warning = binding.recommended?.(name) ? (
        <RecommendedFieldWarning
            control={binding.control}
            name={name}
            intl={binding.intl}
        />
    ) : undefined;
    const recentTrigger = binding.recentTrigger?.(name);
    const hasAdornment = Boolean(
        unitId || options.endAdornment || warning || recentTrigger
    );
    return (
        <TextField
            {...binding.register(name)}
            onKeyDown={binding.recentKeyDown?.(name)}
            label={binding.intl.formatMessage(
                { id: binding.labelId(name) },
                options.labelValues
            )}
            fullWidth
            error={!!error}
            helperText={
                error
                    ? binding.validationMessage(error.message)
                    : options.helperText
            }
            slotProps={{
                inputLabel: options.shrinkLabel ? { shrink: true } : undefined,
                input: {
                    readOnly: binding.readOnly,
                    /**
                     * Written out rather than mapped over a list: these are
                     * three named slots, not a collection, and a fragment over
                     * an array would want keys for them. A measure that is also
                     * "recommended" carries both — the unit next to the value,
                     * the warning icon outermost.
                     */
                    endAdornment: hasAdornment ? (
                        <>
                            {unitId && (
                                <InputAdornment position="end">
                                    {/* Formatted to a STRING on purpose:
                                        InputAdornment wraps a string child in a
                                        `textSecondary` Typography and leaves an
                                        ELEMENT alone, so a <FormattedMessage>
                                        here prints the unit in the input's own
                                        full-strength colour — louder than the
                                        value it qualifies, and louder than the
                                        very same "cm" beside a Schicht. */}
                                    {binding.intl.formatMessage({ id: unitId })}
                                </InputAdornment>
                            )}
                            {options.endAdornment}
                            {recentTrigger && (
                                <InputAdornment position="end">
                                    {recentTrigger}
                                </InputAdornment>
                            )}
                            {warning}
                        </>
                    ) : undefined
                }
            }}
        />
    );
}
